// ============================================================
// 分子グラフの採点ロジック
//
// 構造式を「原子＝ノード / 結合＝重み付きエッジ（結合次数）」の
// ラベル付きグラフとみなし、正解グラフと同型かどうかを判定する。
//
// 同型判定は Weisfeiler-Lehman（色細分化）法で正準署名を作り比較する。
// 高校〜共通テストレベルの小さな分子では、この手法で実用上十分に
// 異性体を区別できる。
// ============================================================

/** 短い文字列ハッシュ（djb2）。署名を短く保つため */
function hashStr(str) {
  let h = 5381
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0
  }
  // 符号なし16進で返す
  return (h >>> 0).toString(36)
}

/**
 * 隣接リストを構築
 * @param {{id:string, element:string}[]} atoms
 * @param {{a:string, b:string, order:number}[]} bonds
 */
function buildAdjacency(atoms, bonds) {
  const adj = new Map()
  atoms.forEach((a) => adj.set(a.id, []))
  bonds.forEach((b) => {
    if (!adj.has(b.a) || !adj.has(b.b) || b.a === b.b) return
    const order = b.order || 1
    adj.get(b.a).push({ id: b.b, order })
    adj.get(b.b).push({ id: b.a, order })
  })
  return adj
}

/**
 * 分子グラフの正準署名（WL法）
 * 同型なグラフは同じ署名を返す
 */
export function moleculeSignature(atoms, bonds) {
  if (!atoms || atoms.length === 0) return 'EMPTY'
  const adj = buildAdjacency(atoms, bonds)

  // 初期色 = 元素記号
  let colors = new Map()
  atoms.forEach((a) => colors.set(a.id, a.element))

  const rounds = Math.max(1, atoms.length)
  for (let r = 0; r < rounds; r++) {
    const next = new Map()
    for (const a of atoms) {
      const neighborSig = (adj.get(a.id) || [])
        .map((n) => `${n.order}:${colors.get(n.id)}`)
        .sort()
        .join('|')
      next.set(a.id, hashStr(`${colors.get(a.id)}#${neighborSig}`))
    }
    colors = next
  }

  const multiset = atoms.map((a) => colors.get(a.id)).sort()
  return multiset.join(',')
}

/**
 * 分子式を計算（例: C2H6O）。表示・部分採点用。
 * Hill 順（C→H→その他アルファベット順）で並べる
 */
export function molecularFormula(atoms) {
  const counts = {}
  atoms.forEach((a) => {
    counts[a.element] = (counts[a.element] || 0) + 1
  })
  const elements = Object.keys(counts)
  const order = []
  if (counts.C) order.push('C')
  if (counts.H) order.push('H')
  elements
    .filter((e) => e !== 'C' && e !== 'H')
    .sort()
    .forEach((e) => order.push(e))
  return order
    .map((e) => (counts[e] > 1 ? `${e}${counts[e]}` : e))
    .join('')
}

/** 元素ごとの原子数マップ */
function elementCounts(atoms) {
  const counts = {}
  atoms.forEach((a) => {
    counts[a.element] = (counts[a.element] || 0) + 1
  })
  return counts
}

/** 2つの原子数マップが一致するか */
function sameElementCounts(a, b) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const k of keys) {
    if ((a[k] || 0) !== (b[k] || 0)) return false
  }
  return true
}

/**
 * ユーザーの構造式を採点する
 * @param {{atoms, bonds}} userGraph
 * @param {{atoms, bonds}} answerGraph
 * @returns 採点結果オブジェクト
 */
export function gradeStructure(userGraph, answerGraph) {
  const userAtoms = userGraph.atoms || []
  const userBonds = userGraph.bonds || []
  const ansAtoms = answerGraph.atoms || []
  const ansBonds = answerGraph.bonds || []

  const userCounts = elementCounts(userAtoms)
  const ansCounts = elementCounts(ansAtoms)

  // 1) 分子式（原子数）の一致 — 50%
  const formulaMatch = sameElementCounts(userCounts, ansCounts)

  // 2) 構造（同型）の一致 — 50%
  const userSig = moleculeSignature(userAtoms, userBonds)
  const ansSig = moleculeSignature(ansAtoms, ansBonds)
  const structureMatch = formulaMatch && userSig === ansSig

  const score = Math.round((formulaMatch ? 50 : 0) + (structureMatch ? 50 : 0))
  const isCorrect = structureMatch

  // 元素ごとの過不足（フィードバック用）
  const elementDiff = {}
  const keys = new Set([...Object.keys(userCounts), ...Object.keys(ansCounts)])
  keys.forEach((k) => {
    const diff = (userCounts[k] || 0) - (ansCounts[k] || 0)
    if (diff !== 0) elementDiff[k] = diff // +なら過剰, -なら不足
  })

  return {
    isCorrect,
    score,
    formulaMatch,
    structureMatch,
    formulaUser: molecularFormula(userAtoms),
    formulaAnswer: molecularFormula(ansAtoms),
    bondCountUser: userBonds.length,
    bondCountAnswer: ansBonds.length,
    elementDiff,
  }
}
